import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "sss-cart-v1";

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    const addItem = (item) => {
        setItems((prev) => {
            const key = `${item.product_id}__${item.size}`;
            const existing = prev.find((p) => `${p.product_id}__${p.size}` === key);
            if (existing) {
                return prev.map((p) =>
                    `${p.product_id}__${p.size}` === key
                        ? { ...p, quantity: p.quantity + item.quantity }
                        : p
                );
            }
            return [...prev, item];
        });
        setIsOpen(true);
    };

    const updateQuantity = (productId, size, quantity) => {
        setItems((prev) =>
            prev
                .map((p) =>
                    p.product_id === productId && p.size === size
                        ? { ...p, quantity: Math.max(0, quantity) }
                        : p
                )
                .filter((p) => p.quantity > 0)
        );
    };

    const removeItem = (productId, size) => {
        setItems((prev) => prev.filter((p) => !(p.product_id === productId && p.size === size)));
    };

    const clearCart = () => setItems([]);

    const subtotal = useMemo(
        () => items.reduce((sum, it) => sum + it.unit_price * it.quantity, 0),
        [items]
    );

    const count = useMemo(
        () => items.reduce((sum, it) => sum + it.quantity, 0),
        [items]
    );

    const value = {
        items,
        addItem,
        updateQuantity,
        removeItem,
        clearCart,
        subtotal,
        count,
        isOpen,
        setIsOpen,
    };

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used inside CartProvider");
    return ctx;
};
